import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Activity, Gauge, TrendingUp, Zap, Sun, Thermometer, Wind, Navigation, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
import { KpiNames, realtimeOmpMapping, weatherMonitoring } from '../../constants/kpi-constants';
import moment from 'moment';
import { useToast } from '../../hooks/use-toast';
import useDailySummary from '../../hooks/use-daily-summary';
import { useLocation } from 'react-router-dom';

const TechnicalParameters = () => {
  const [keyPerfIndicators, setKeyPerfIndicators] = useState([]);
  const [realTimeMonitoring, setRealTimeMonitoring] = useState([]);
  const [weatherMonitoringData, setWeatherMonitoringData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const { toast } = useToast();

  // Daily summary hook
  const { summary: dailySummary } = useDailySummary();

  const [activeTab, setActiveTab] = useState('kpi'); // 'summary' | 'kpi'

  const location = useLocation();
  useEffect(()=>{
    if(location.pathname.includes('/plant/summary')){
      setActiveTab('summary');
    }
  },[location.pathname]);

  // Auto-refresh interval (5 minutes)
  useEffect(() => {
    loadAllData();
    const refreshInterval = setInterval(() => {
      loadAllData();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  const loadAllData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([
        loadKeyPerfIndicationData(),
        loadRealTimeMonitoringData(),
        loadWeatherMonitoringData()
      ]);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load technical parameters",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadKeyPerfIndicationData = async () => {
    try {
      const formattedDate = moment().format('YYYY-MM-DD');
      const requestBody = {
        start_date: formattedDate + ' 00:00:00',
        end_date: formattedDate + ' 23:55:00',
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID],
        tags_list: Object.keys(KpiNames)
      };

      const response = await apiService.getKpiReadingsByTags(requestBody);
      const kpiResults = [];

      if (response.data?.length) {
        const lastReading = response.data[response.data.length - 1];
        if (lastReading?.readings?.length && lastReading.readings[0]?.KPI?.length) {
          const kpiData = lastReading.readings[0].KPI;
          
          Object.keys(KpiNames).forEach(kpiKey => {
            const kpiValue = kpiData.find(item => item[kpiKey]);
            kpiResults.push({
              name: KpiNames[kpiKey].name,
              value: kpiValue?.[kpiKey] !== null && kpiValue?.[kpiKey] !== undefined 
                ? Number(kpiValue[kpiKey]).toFixed(2) 
                : '-',
              code: kpiKey
            });
          });
        }
      }

      setKeyPerfIndicators(kpiResults);
    } catch (error) {
      console.error('Error loading KPI data:', error);
      // Set default values on error
      setKeyPerfIndicators(
        Object.keys(KpiNames).map(key => ({
          name: KpiNames[key].name,
          value: '-',
          code: key
        }))
      );
    }
  };

  const loadRealTimeMonitoringData = async () => {
    try {
      const formattedDate = moment().format('YYYY-MM-DD');
      const requestBody = {
        start_date: formattedDate + ' 00:00:00',
        end_date: formattedDate + ' 23:55:00',
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.DP_ACTIVE_POWER_ID],
        tags_list: Object.keys(realtimeOmpMapping)
      };

      const response = await apiService.getOmpDataByTags(requestBody);
      const ompResults = [];
      
      if (response.data?.json_object?.length) {
        const lastReading = response.data.json_object[response.data.json_object.length - 1];
        
        Object.keys(realtimeOmpMapping).forEach(ompKey => {
          const responseKey = `DP_PANEL_${ompKey}`;
          let value = lastReading[responseKey];
          
          if (value !== null && value !== undefined) {
            // Apply specific transformations
            if (responseKey === 'DP_APPARENT_POWER') {
              value = (Number(value) * 1000).toFixed(2);
            } else if (ompKey === 'DP_RMS_PHASE_RYB_VOLTAGE') {
              value = (Number(value) / 1000).toFixed(2);
            } else {
              value = Number(value).toFixed(2);
            }
          } else {
            value = '-';
          }
          
          ompResults.push({
            name: realtimeOmpMapping[ompKey].name,
            value: value,
            code: ompKey,
            icon: realtimeOmpMapping[ompKey].icon
          });
        });
        
        // Calculate specific power if needed
        if (ompResults.find(item => item.code === 'DP_SPECIFIC_POWER')) {
          await calculateSpecificPower(ompResults);
        }
      }

      setRealTimeMonitoring(ompResults);
    } catch (error) {
      console.error('Error loading real-time monitoring data:', error);
      setRealTimeMonitoring(
        Object.keys(realtimeOmpMapping).map(key => ({
          name: realtimeOmpMapping[key].name,
          value: '-',
          code: key,
          icon: realtimeOmpMapping[key].icon
        }))
      );
    }
  };

  const calculateSpecificPower = async (ompResults) => {
    try {
      const request = {
        plant_id: API_CONFIG.PLANT_ID,
        asset_type: 'PLANT',
        tag_list: ['dc_capacity_actual']
      };
      
      const response = await apiService.fetchApoAssetDataForAssetTable(request);
      
      if (response.data?.json_object?.assets?.length) {
        const dcCapacityActual = response.data.json_object.assets[0].dc_capacity_actual;
        const activePowerData = ompResults.find(item => item.code === 'DP_ACTIVE_POWER');
        const specificPowerData = ompResults.find(item => item.code === 'DP_SPECIFIC_POWER');
        
        if (activePowerData?.value !== '-' && dcCapacityActual && specificPowerData) {
          specificPowerData.value = (Number(activePowerData.value) / Number(dcCapacityActual)).toFixed(2);
        }
      }
    } catch (error) {
      console.error('Error calculating specific power:', error);
    }
  };

  const loadWeatherMonitoringData = async () => {
    try {
      const formattedDate = moment().format('YYYY-MM-DD');
      const requestBody = {
        start_date: formattedDate + ' 00:00:00',
        end_date: formattedDate + ' 23:55:00',
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.WS_LIVE_POA_POWER_DENSITY_ID],
        tags_list: Object.keys(weatherMonitoring)
      };

      const response = await apiService.getOmpDataByTags(requestBody);
      const weatherResults = [];
      
      if (response.data?.json_object?.length) {
        const lastReading = response.data.json_object[response.data.json_object.length - 1];
        
        Object.keys(weatherMonitoring).forEach(weatherKey => {
          const responseKey = Object.keys(lastReading).find(key => key.includes(weatherKey));
          let value = responseKey ? lastReading[responseKey] : null;
          
          if (value !== null && value !== undefined) {
            value = Number(value).toFixed(2);
          } else {
            value = '-';
          }
          
          weatherResults.push({
            name: weatherMonitoring[weatherKey].name,
            value: value,
            code: weatherKey,
            icon: weatherMonitoring[weatherKey].icon
          });
        });
      }

      setWeatherMonitoringData(weatherResults);
    } catch (error) {
      console.error('Error loading weather monitoring data:', error);
      setWeatherMonitoringData(
        Object.keys(weatherMonitoring).map(key => ({
          name: weatherMonitoring[key].name,
          value: '-',
          code: key,
          icon: weatherMonitoring[key].icon
        }))
      );
    }
  };

  const getIconComponent = (iconName) => {
    const icons = {
      Zap, Sun, Thermometer, Wind, Navigation, Activity, TrendingUp
    };
    return icons[iconName] || Zap;
  };

  const renderKpiCard = (kpi, index) => (
    <Card key={index} className="hover:shadow-lg transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{kpi.name}</p>
            <p className="text-2xl font-bold mt-1">{kpi.value}</p>
          </div>
          {kpi.icon && (() => {
            const Icon = getIconComponent(kpi.icon);
            return <Icon className="h-8 w-8 text-blue-500" />;
          })()}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Technical Parameters</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Real-time monitoring of key technical parameters
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center space-x-2 bg-green-100 dark:bg-green-900/20 px-3 py-1 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-800 dark:text-green-200">LIVE</span>
          </div>
          <span className="text-sm text-gray-500">
            Last updated: {moment(lastRefresh).format('HH:mm:ss')}
          </span>
          <Button onClick={loadAllData} disabled={isLoading} size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-3 my-4">
        <Button variant={activeTab==='kpi'? 'default':'outline'} size="sm" onClick={()=>setActiveTab('kpi')}>Key Performance</Button>
        <Button variant={activeTab==='summary'? 'default':'outline'} size="sm" onClick={()=>setActiveTab('summary')}>Daily Summary</Button>
      </div>

      {activeTab==='summary' && dailySummary && (
        <Card>
          <CardHeader>
            <CardTitle>Summary (Daily)</CardTitle>
            <CardDescription>{moment().format('DD MMM YYYY')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Plant Description */}
              <div>
                <h4 className="text-blue-600 font-semibold mb-2">Plant Description</h4>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-4 text-center">
                  {[
                    { label: 'Generation Start Time', val: dailySummary.generation_start_time, unit: 'Hrs' },
                    { label: 'Generation End Time', val: dailySummary.generation_end_time, unit: 'Hrs' },
                    { label: 'Total Generation Time', val: dailySummary.total_generation_time, unit: 'Hrs' },
                    { label: 'Total Hrs GPOA > 100', val: dailySummary.total_hrs_gpoa_gt_100, unit: '(W/m^2 Irradiance)' },
                    { label: 'Total Hrs GPOA > 500', val: dailySummary.total_hrs_gpoa_gt_500, unit: '(W/m^2 Irradiance)' },
                    { label: 'Peak Maximum Power', val: dailySummary.peak_maximum_power, unit: 'MW' },
                    { label: 'Peak Maximum POA', val: dailySummary.peak_maximum_poa, unit: '(W/m^2)' }
                  ].map((item,i)=>(
                    <div key={i}>
                      <p className="text-xl font-bold">{item.val ?? '-'}</p>
                      <p className="text-xs text-muted-foreground">{item.label}</p>
                      {item.unit && <p className="text-[10px] text-gray-400">{item.unit}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Export Description */}
              <div>
                <h4 className="text-blue-600 font-semibold mb-2">Export Description</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  {[
                    { label: 'POI-1 Exported Energy', val: dailySummary.poi1_exported_energy, unit: 'MWh' },
                    { label: 'POI-2 Exported Energy', val: dailySummary.poi2_exported_energy, unit: 'MWh' },
                    { label: 'Total Exported Energy', val: dailySummary.total_exported_energy, unit: 'MWh' },
                    { label: 'Total Imported Energy', val: dailySummary.total_imported_energy, unit: 'MWh' }
                  ].map((it,j)=>(
                    <div key={j}>
                      <p className="text-xl font-bold">{it.val ?? '-'}</p>
                      <p className="text-xs text-muted-foreground">{it.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Loss / Availability Description */}
              <div>
                <h4 className="text-blue-600 font-semibold mb-2">Loss / Availability Description</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  {[
                    { label: 'Inverter Availability (%)', val: dailySummary.inverter_availability },
                    { label: 'Soiling Loss (%)', val: dailySummary.soiling_loss },
                    { label: 'Inverter Breakdown Loss (%)', val: dailySummary.inverter_breakdown_loss },
                    { label: 'Module Temperature Loss (%)', val: dailySummary.module_temperature_loss },
                    { label: 'Total Energy Loss (MWh)', val: dailySummary.total_energy_loss }
                  ].map((l,k)=>(
                    <div key={k}>
                      <p className="text-xl font-bold">{l.val ?? '-'}</p>
                      <p className="text-xs text-muted-foreground">{l.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab==='kpi' && (
      <Card>
        <CardHeader>
          <CardTitle>Key Performance Indicators</CardTitle>
          <CardDescription>Today's plant performance metrics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-8 w-1/2" />
                  </CardContent>
                </Card>
              ))
            ) : (
              keyPerfIndicators.map((kpi, index) => renderKpiCard(kpi, index))
            )}
          </div>
        </CardContent>
      </Card>
      )}

      {activeTab==='kpi' && (
      <Card>
          <CardHeader>
          <CardTitle>Real-time Monitoring</CardTitle>
          <CardDescription>Live operational parameters</CardDescription>
          </CardHeader>
          <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-8 w-1/2" />
          </CardContent>
                </Card>
              ))
            ) : (
              realTimeMonitoring.map((omp, index) => renderKpiCard(omp, index))
            )}
                    </div>
              </CardContent>
      </Card>
      )}

      {activeTab==='kpi' && (
      <Card>
        <CardHeader>
          <CardTitle>Weather Monitoring</CardTitle>
          <CardDescription>Environmental conditions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-3/4 mb-2" />
                    <Skeleton className="h-8 w-1/2" />
                  </CardContent>
                </Card>
              ))
            ) : (
              weatherMonitoringData.map((weather, index) => renderKpiCard(weather, index))
            )}
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default TechnicalParameters;